import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Package, User, Home, FileText, Save, Search, Plus, MapPin, Printer, HelpCircle } from 'lucide-react';
import apiClient from '../services/api';
import { printConsignment } from '../utils/printUtils';

const PRIMARY      = '#2563EB';
const PRIMARY_DARK = '#1D4ED8';
const SUCCESS      = '#16A34A';
const SUCCESS_DARK = '#15803D';

const CONSIGNMENT_TYPE = 'D';

const ConsignmentEntryFormD = ({ onClose, onSave, editData = null }) => {
  const [loading,          setLoading]          = useState(false);
  const [error,            setError]            = useState('');
  const [employees,        setEmployees]        = useState([]);
  const [customers,        setCustomers]        = useState([]);
  const [cities,           setCities]           = useState([]);
  const [generatingNumber, setGeneratingNumber] = useState(false);

  const [showCustomerModal,   setShowCustomerModal]   = useState(false);
  const [customerModalType,   setCustomerModalType]   = useState('');
  const [customerSearchTerm,  setCustomerSearchTerm]  = useState('');
  const [showAddCustomerForm, setShowAddCustomerForm] = useState(false);
  const [showHelp,             setShowHelp]           = useState(false);

  const getSessionData = () => {
    try {
      const s = localStorage.getItem('ltms_session');
      if (!s) return null;
      const d = JSON.parse(s);
      return d?.session || d;
    } catch { return null; }
  };

  const session   = getSessionData();
  const companyId = session?.companyid;

  const [formData, setFormData] = useState({
    ConsignmentNo: '',
    ConsignmentDate: (() => { const now = new Date(); return new Date(now - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16); })(),
    ConsignmentType: CONSIGNMENT_TYPE,
    OriginCityID: '', DestinationCityID: '',
    ShipperID: '', ShipperCode: '', ShipperName: '', ShipperContactPerson: '',
    ShipperFlatBuilding: '', ShipperStreet: '', ShipperPhone: '', ShipperCity: '',
    ConsigneeID: '', ConsigneeCode: '', ConsigneeName: '', ConsigneeContactPerson: '',
    ConsigneeFlatBuilding: '', ConsigneeStreet: '', ConsigneePhone: '', ConsigneeCity: '',
    Description: '', NoOfPkgs: '', Weight: '', Pieces: '', InvoiceValue: '',
    PaymentMode: '', CourierStatusID: '', DeliveryDate: '',
    CollectedBy: '', ReceivedBy: '', Remarks: '',
  });

  const [newCustomerData, setNewCustomerData] = useState({
    customername: '', customertype: 'CR', phone: '', email: '', address: '', cityid: '', taxnumber: '',
  });

  useEffect(() => {
    const initializeForm = async () => {
      if (editData && editData.ConsignmentID) {
        const ed = editData;
        const fixDateTime = (val) => {
          if (!val || val === '0000-00-00 00:00:00' || val === '0000-00-00') return '';
          if (val.includes('T')) return val.slice(0, 16);
          if (val.includes(' ') && val.length > 10) return val.replace(' ', 'T').slice(0, 16);
          return val.slice(0, 10) + 'T00:00';
        };
        const fixDate = (val) => (!val || val === '0000-00-00') ? '' : val.slice(0, 10);

        setFormData({
          ConsignmentNo: ed.ConsignmentNo || '', ConsignmentDate: fixDateTime(ed.ConsignmentDate),
          ConsignmentType: CONSIGNMENT_TYPE,
          OriginCityID: String(ed.OriginCityID || ed.OriginID || ''),
          DestinationCityID: String(ed.DestinationCityID || ed.DestinationID || ''),
          ShipperID: String(ed.ShipperID || ed.ConsignorID || ''),
          ShipperCode: ed.ShipperCode || '', ShipperName: ed.ShipperName || ed.Shipper || '',
          ShipperContactPerson: ed.ShipperContactPerson || ed.ShipperContactName || '',
          ShipperFlatBuilding: ed.ShipperFlatBuilding || ed.ShipperLocation || '',
          ShipperStreet: ed.ShipperStreet || '', ShipperPhone: ed.ShipperPhone || '',
          ShipperCity: ed.ShipperCity || '',
          ConsigneeID: String(ed.ConsigneeID || ''),
          ConsigneeCode: ed.ConsigneeCode || '', ConsigneeName: ed.ConsigneeName || ed.Consignee || '',
          ConsigneeContactPerson: ed.ConsigneeContactPerson || ed.ConsigneeContactName || '',
          ConsigneeFlatBuilding: ed.ConsigneeFlatBuilding || ed.ConsigneeLocation || '',
          ConsigneeStreet: ed.ConsigneeStreet || '', ConsigneePhone: ed.ConsigneePhone || '',
          ConsigneeCity: ed.ConsigneeCity || '',
          Description: ed.Description || '', NoOfPkgs: ed.NoOfPkgs || '',
          Weight: ed.Weight || '', Pieces: ed.Pieces || '', InvoiceValue: ed.InvoiceValue || '',
          PaymentMode: ed.PaymentMode || '', CourierStatusID: String(ed.CourierStatusID || ''),
          DeliveryDate: fixDate(ed.DeliveryDate),
          CollectedBy: String(ed.CollectedBy || ''), ReceivedBy: String(ed.ReceivedBy || ''),
          Remarks: ed.Remarks || '',
        });
        loadDropdownData();
      } else {
        await loadDropdownData();
        setGeneratingNumber(true);
        await generateConsignmentNumber();
        setGeneratingNumber(false);
      }
    };
    initializeForm();
  }, [editData]);

  const loadDropdownData = async () => {
    try {
      const [empRes, custRes, cityRes] = await Promise.all([
        apiClient.get('/Employees.php').catch(() => ({ data: { success: false } })),
        apiClient.get('/Customers.php').catch(() => ({ data: { success: false } })),
        apiClient.get('/Cities.php').catch(() => ({ data: { success: false } })),
      ]);
      if (empRes.data.success)  setEmployees(empRes.data.data || []);
      if (custRes.data.success) setCustomers(custRes.data.data || []);
      if (cityRes.data.success) setCities(cityRes.data.data || []);
    } catch (err) { console.error(err); setError('Failed to load dropdown data'); }
  };

  const generateConsignmentNumber = async () => {
    try {
      const res = await apiClient.get(`/ConsignmentsD.php/next-number?type=${CONSIGNMENT_TYPE}`);
      if (res.data.success && res.data.data?.consignmentNo) {
        setFormData(p => ({ ...p, ConsignmentNo: res.data.data.consignmentNo }));
      } else throw new Error('Invalid API response');
    } catch {
      const fb = `CNS-D-${Date.now()}`;
      setFormData(p => ({ ...p, ConsignmentNo: fb }));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const openCustomerModal = (type) => {
    setCustomerModalType(type); setCustomerSearchTerm('');
    setShowAddCustomerForm(false); setShowCustomerModal(true);
  };

  const selectCustomer = (customer) => {
    if (customerModalType === 'shipper') {
      setFormData(p => ({ ...p, ShipperID: customer.customerid, ShipperCode: customer.customercode, ShipperName: customer.customername, ShipperPhone: customer.phone || '', ShipperCity: customer.cityname || '' }));
    } else {
      setFormData(p => ({ ...p, ConsigneeID: customer.customerid, ConsigneeCode: customer.customercode, ConsigneeName: customer.customername, ConsigneePhone: customer.phone || '', ConsigneeCity: customer.cityname || '' }));
    }
    setShowCustomerModal(false);
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!newCustomerData.customername.trim()) { alert('Customer name is required'); return; }
    try {
      const res = await apiClient.post('/Customers.php', { ...newCustomerData, companyid: companyId, isactive: 1 });
      if (res.data.success) {
        await loadDropdownData();
        selectCustomer({ customerid: res.data.data.customerid, customercode: res.data.data.customercode, customername: newCustomerData.customername, phone: newCustomerData.phone, cityname: cities.find(c => c.cityid === parseInt(newCustomerData.cityid))?.cityname || '' });
        setNewCustomerData({ customername: '', customertype: 'CR', phone: '', email: '', address: '', cityid: '', taxnumber: '' });
        setShowAddCustomerForm(false);
        alert(`Customer created!\nCode: ${res.data.data.customercode}`);
      } else { alert('Error: ' + res.data.message); }
    } catch (err) { alert('Error adding customer: ' + (err.response?.data?.message || err.message)); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.ConsignmentNo.trim())    { setError('Consignment number is required'); return; }
    if (!formData.ShipperName.trim())      { setError('Shipper name is required'); return; }
    if (!formData.ConsigneeName.trim())    { setError('Consignee name is required'); return; }
    if (!formData.OriginCityID)           { setError('Origin city is required'); return; }
    if (!formData.DestinationCityID)      { setError('Destination city is required'); return; }
    setLoading(true); setError('');
    try {
      const dataToSave = { ...formData, ConsignmentType: CONSIGNMENT_TYPE, ShipperID: formData.ShipperID || null, ConsigneeID: formData.ConsigneeID || null, CollectedBy: formData.CollectedBy || null, ReceivedBy: formData.ReceivedBy || null, CourierStatusID: formData.CourierStatusID || null, OriginCityID: formData.OriginCityID || null, DestinationCityID: formData.DestinationCityID || null, NoOfPkgs: formData.NoOfPkgs || 0, Weight: formData.Weight || 0, Pieces: formData.Pieces || 0, InvoiceValue: formData.InvoiceValue || 0 };
      if (editData?.ConsignmentID) dataToSave.ConsignmentID = editData.ConsignmentID;
      await onSave(dataToSave);
    } catch (err) { setError(err.message || 'Failed to save consignment'); }
    finally { setLoading(false); }
  };

  const filteredCustomers = customers.filter(c =>
    c.customername?.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
    c.customercode?.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
    c.phone?.includes(customerSearchTerm)
  );

  // shared input/select/textarea classes
  const inpCls  = `w-full px-3 py-2.5 border border-[#CBD5E1] rounded-lg text-sm bg-white outline-none transition-all duration-150 focus:border-[#2563EB] focus:shadow-[0_0_0_3px_rgba(37,99,235,0.12)] font-[inherit]`;
  const numCls  = inpCls + ' text-right font-mono font-semibold tracking-wide';
  const selCls  = inpCls + ' cursor-pointer';
  const lblCls  = 'block mb-1.5 text-sm font-medium text-gray-700';

  const SectionTitle = ({ icon: Icon, title, action }) => (
    <div className="flex items-center gap-2.5 mb-4 pb-3 border-b-2 border-gray-100">
      <Icon size={16} className="text-[#2563EB] flex-shrink-0" />
      <span className="text-[15px] font-semibold text-gray-800 flex-1">{title}</span>
      {action}
    </div>
  );

  const FormSection = ({ children, className = '' }) => (
    <div className={`bg-white rounded-xl p-5 mb-4 border border-[#E2E8F0] shadow-sm ${className}`}>{children}</div>
  );

  const SelectBtn = ({ type }) => (
    <button type="button" onClick={() => openCustomerModal(type)}
      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white border-0 rounded-lg cursor-pointer"
      style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)` }}>
      <Search size={12} /> Select
    </button>
  );

  // ── Customer Modal (portal) ─────────────────────────────────────────────────
  const customerModalJSX = showCustomerModal ? ReactDOM.createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-5" style={{ background: 'rgba(0,0,0,0.65)', zIndex: 999999 }}>
      <div className="bg-white rounded-xl w-full max-w-[800px] max-h-[90vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* modal header */}
        <div className="flex items-center justify-between px-6 py-4 rounded-t-xl flex-shrink-0"
          style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)` }}>
          <h3 className="text-lg font-semibold m-0" style={{ color: '#fff' }}>
            Select {customerModalType === 'shipper' ? 'Shipper' : 'Consignee'}
          </h3>
          <button onClick={() => setShowCustomerModal(false)}
            className="w-8 h-8 flex items-center justify-center rounded-lg border-0 cursor-pointer bg-white/20 text-white hover:bg-white/30 transition-colors duration-150"
            style={{ color: '#fff' }}>
            <X size={18} />
          </button>
        </div>

        {/* modal body */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* search */}
          <div className="flex items-center gap-2 px-3 py-2.5 border-2 border-[#CBD5E1] rounded-lg bg-white mb-4 focus-within:border-[#2563EB] transition-colors duration-150">
            <Search size={18} className="text-gray-400" />
            <input type="text" placeholder="Search customers..." value={customerSearchTerm}
              onChange={e => setCustomerSearchTerm(e.target.value)} autoFocus
              className="flex-1 border-0 outline-none text-sm" />
          </div>

          {/* add new */}
          <button type="button" onClick={() => setShowAddCustomerForm(!showAddCustomerForm)}
            className="flex items-center gap-1.5 px-3 py-2 mb-4 text-sm font-bold text-white border-0 rounded-lg cursor-pointer"
            style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)` }}>
            <Plus size={14} /> Add New Customer
          </button>

          {showAddCustomerForm && (
            <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-[#E2E8F0]">
              <h4 className="text-sm font-bold text-gray-700 mb-3">New Customer</h4>
              <form onSubmit={handleAddCustomer}>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={lblCls}>Name *</label><input type="text" value={newCustomerData.customername} onChange={e => setNewCustomerData({ ...newCustomerData, customername: e.target.value })} required className={inpCls} /></div>
                  <div><label className={lblCls}>Phone</label><input type="text" value={newCustomerData.phone} onChange={e => setNewCustomerData({ ...newCustomerData, phone: e.target.value })} className={inpCls} /></div>
                  <div><label className={lblCls}>City</label>
                    <select value={newCustomerData.cityid} onChange={e => setNewCustomerData({ ...newCustomerData, cityid: e.target.value })} className={selCls}>
                      <option value="">Select City</option>
                      {cities.map(c => <option key={c.cityid} value={c.cityid}>{c.cityname}</option>)}
                    </select>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button type="submit" className="px-3 py-1.5 text-xs font-bold text-white border-0 rounded-lg cursor-pointer" style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)` }}>Create & Select</button>
                  <button type="button" onClick={() => setShowAddCustomerForm(false)} className="px-3 py-1.5 text-xs font-bold text-gray-700 bg-white border border-[#CBD5E1] rounded-lg cursor-pointer hover:bg-gray-50">Cancel</button>
                </div>
              </form>
            </div>
          )}

          {/* customer table */}
          <div className="max-h-[380px] overflow-y-auto rounded-lg border border-[#E2E8F0]">
            <table className="w-full border-collapse text-sm">
              <thead className="bg-[#F1F5F9] sticky top-0">
                <tr>{['Code', 'Name', 'Phone', 'City', 'Action'].map(h => (
                  <th key={h} className="px-3 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-500 border-b border-[#E2E8F0]">{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {filteredCustomers.length === 0 ? (
                  <tr><td colSpan="5" className="py-8 text-center text-gray-400 text-sm">No customers found</td></tr>
                ) : filteredCustomers.map(c => (
                  <tr key={c.customerid} className="border-b border-gray-100 hover:bg-[#EFF6FF] transition-colors duration-150">
                    <td className="px-3 py-2.5 font-mono text-xs text-gray-600">{c.customercode}</td>
                    <td className="px-3 py-2.5 font-medium text-gray-800">{c.customername}</td>
                    <td className="px-3 py-2.5 text-gray-500">{c.phone}</td>
                    <td className="px-3 py-2.5 text-gray-500">{c.cityname}</td>
                    <td className="px-3 py-2.5">
                      <button type="button" onClick={() => selectCustomer(c)}
                        className="px-3 py-1.5 text-xs font-bold text-white border-0 rounded-lg cursor-pointer"
                        style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)` }}>
                        Select
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  // ── Help Modal (portal) ─────────────────────────────────────────────────────
  const helpModalJSX = showHelp ? ReactDOM.createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-5" style={{ background: 'rgba(0,0,0,0.65)', zIndex: 999999 }}
      onClick={() => setShowHelp(false)}>
      <div className="bg-white rounded-xl w-full max-w-[640px] max-h-[85vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 rounded-t-xl flex-shrink-0"
          style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)` }}>
          <h3 className="flex items-center gap-2 text-lg font-semibold m-0" style={{ color: '#fff' }}>
            <HelpCircle size={18} /> How to Fill This Form
          </h3>
          <button onClick={() => setShowHelp(false)}
            className="w-8 h-8 flex items-center justify-center rounded-lg border-0 cursor-pointer bg-white/20 text-white hover:bg-white/30 transition-colors duration-150">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {[
            { title: 'Basic Information', desc: 'The Consignment No is auto-generated — you don\'t need to type it. Set the date and time the shipment is being booked.' },
            { title: 'Route', desc: 'Choose the Origin (pickup city) and Destination (delivery city). Both are required.' },
            { title: 'Shipper / Consignee', desc: 'Click "Select" to pick an existing customer, or fill the Name field manually for a one-off shipper/consignee. Use "Add New Customer" inside the Select popup to save a new customer for future use.' },
            { title: 'Package Details', desc: 'Enter weight, pieces, number of packages, and invoice value. Choose a Payment Mode (Cash, Credit, or Cheque).' },
            { title: 'Delivery Details', desc: 'Optionally assign who collected and received the shipment, set its current Status, and add a delivery date or remarks.' },
            { title: 'Save', desc: 'Click "Save Domestic Consignment" at the bottom. Once saved (edit mode), you can print the consignment using the Print button.' },
          ].map(({ title, desc }, i) => (
            <div key={i} className="flex gap-3">
              <div className="w-7 h-7 flex-shrink-0 rounded-full flex items-center justify-center text-xs font-bold text-white"
                style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)` }}>
                {i + 1}
              </div>
              <div>
                <div className="text-sm font-bold text-gray-800 mb-0.5">{title}</div>
                <div className="text-sm text-gray-600">{desc}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end px-6 py-4 border-t border-[#E2E8F0] flex-shrink-0">
          <button onClick={() => setShowHelp(false)}
            className="px-5 py-2.5 text-sm font-bold text-white border-0 rounded-lg cursor-pointer"
            style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)` }}>
            Got it
          </button>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  if (!companyId) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-5 z-[9999]">
        <div className="bg-white rounded-xl w-full max-w-[500px] p-8 text-center shadow-2xl">
          <p className="text-gray-600 mb-4">Please select a company and financial year from the dashboard.</p>
          <button onClick={onClose} className="px-4 py-2 text-white border-0 rounded-lg cursor-pointer" style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)` }}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* ── OVERLAY ── */}
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-5 overflow-y-auto" style={{ zIndex: 9999 }}>
        <div className="bg-white rounded-xl w-full max-w-[1200px] max-h-[95vh] flex flex-col shadow-2xl"
          style={{ animation: 'slideIn 0.3s ease-out' }}>

          {/* Header */}
          <div className="flex items-center justify-between px-8 py-6 rounded-t-xl flex-shrink-0 border-b-4"
            style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`, borderBottomColor: '#1E40AF' }}>
            <div className="flex items-center gap-6 flex-1">
              <div className="w-14 h-14 rounded-xl bg-white/30 flex items-center justify-center flex-shrink-0 shadow-lg">
                <Home size={24} color="#fff" />
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <span className="text-[1.6rem] font-bold" style={{ color: '#fff', textShadow: '0 2px 4px rgba(0,0,0,0.4)' }}>
                  {editData ? 'Edit' : 'New'} Consignment — Domestic (D)
                </span>
                <span className="font-mono text-sm font-semibold px-3 py-1.5 rounded-lg border border-white/40 bg-white/25 whitespace-nowrap" style={{ color: '#fff' }}>
                  📋 {generatingNumber ? 'Generating...' : (formData.ConsignmentNo || 'Loading...')}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button type="button" onClick={() => setShowHelp(true)} title="How to fill this form"
                className="w-11 h-11 flex items-center justify-center rounded-xl border-2 border-white/30 bg-white/20 cursor-pointer transition-all duration-150 hover:bg-white/35"
                style={{ color: '#fff' }}>
                <HelpCircle size={20} color="#fff" />
              </button>
              <button onClick={onClose}
                className="w-11 h-11 flex items-center justify-center rounded-xl border-2 border-white/30 bg-white/20 cursor-pointer transition-all duration-150 hover:bg-white/35 hover:rotate-90"
                style={{ color: '#fff' }}>
                <X size={20} color="#fff" />
              </button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 px-6 py-3 mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm font-medium flex-shrink-0">
              <strong>Error:</strong> {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto px-6 py-5 bg-[#F8FAFC]">

              {/* Basic Info */}
              <FormSection>
                <SectionTitle icon={FileText} title="Basic Information (Domestic)" />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={lblCls}>Consignment No *</label>
                    <input type="text" name="ConsignmentNo" value={generatingNumber ? 'Loading...' : formData.ConsignmentNo} readOnly
                      className={`w-full px-3 py-2.5 border rounded-lg text-sm font-bold text-[1.05rem] outline-none ${generatingNumber ? 'bg-amber-50 text-amber-700 border-amber-300' : 'bg-[#EFF6FF] text-gray-900 border-[#93C5FD]'}`} />
                  </div>
                  <div>
                    <label className={lblCls}>Consignment Date & Time *</label>
                    <input type="datetime-local" name="ConsignmentDate" value={formData.ConsignmentDate} onChange={handleChange} required className={inpCls} />
                  </div>
                </div>
              </FormSection>

              {/* Route */}
              <FormSection>
                <SectionTitle icon={MapPin} title="Consignment Route" />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={lblCls}>Origin (Pickup City) *</label>
                    <select name="OriginCityID" value={formData.OriginCityID} onChange={handleChange} required className={selCls}>
                      <option value="">-- Select Origin City --</option>
                      {cities.map(c => <option key={c.cityid} value={c.cityid}>{c.cityname}{c.statename ? ` (${c.statename})` : ''}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={lblCls}>Destination (Delivery City) *</label>
                    <select name="DestinationCityID" value={formData.DestinationCityID} onChange={handleChange} required className={selCls}>
                      <option value="">-- Select Destination City --</option>
                      {cities.map(c => <option key={c.cityid} value={c.cityid}>{c.cityname}{c.statename ? ` (${c.statename})` : ''}</option>)}
                    </select>
                  </div>
                </div>
              </FormSection>

              {/* Shipper */}
              <FormSection>
                <SectionTitle icon={User} title="Shipper | Sender" action={<SelectBtn type="shipper" />} />
                <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
                  <div><label className={lblCls}>Customer Code</label><input type="text" name="ShipperCode" value={formData.ShipperCode} readOnly className={inpCls + ' bg-gray-100 cursor-not-allowed'} /></div>
                  <div><label className={lblCls}>Name *</label><input type="text" name="ShipperName" value={formData.ShipperName} onChange={handleChange} required className={inpCls} /></div>
                  <div><label className={lblCls}>Contact Person</label><input type="text" name="ShipperContactPerson" value={formData.ShipperContactPerson} onChange={handleChange} className={inpCls} /></div>
                  <div><label className={lblCls}>Phone</label><input type="text" name="ShipperPhone" value={formData.ShipperPhone} onChange={handleChange} className={inpCls} /></div>
                  <div><label className={lblCls}>Building</label><input type="text" name="ShipperFlatBuilding" value={formData.ShipperFlatBuilding} onChange={handleChange} className={inpCls} /></div>
                  <div><label className={lblCls}>Street</label><input type="text" name="ShipperStreet" value={formData.ShipperStreet} onChange={handleChange} className={inpCls} /></div>
                  <div><label className={lblCls}>City</label><input type="text" name="ShipperCity" value={formData.ShipperCity} onChange={handleChange} className={inpCls} /></div>
                </div>
              </FormSection>

              {/* Consignee */}
              <FormSection>
                <SectionTitle icon={User} title="Consignee | Receiver" action={<SelectBtn type="consignee" />} />
                <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
                  <div><label className={lblCls}>Customer Code</label><input type="text" name="ConsigneeCode" value={formData.ConsigneeCode} readOnly className={inpCls + ' bg-gray-100 cursor-not-allowed'} /></div>
                  <div><label className={lblCls}>Name *</label><input type="text" name="ConsigneeName" value={formData.ConsigneeName} onChange={handleChange} required className={inpCls} /></div>
                  <div><label className={lblCls}>Contact Person</label><input type="text" name="ConsigneeContactPerson" value={formData.ConsigneeContactPerson} onChange={handleChange} className={inpCls} /></div>
                  <div><label className={lblCls}>Phone</label><input type="text" name="ConsigneePhone" value={formData.ConsigneePhone} onChange={handleChange} className={inpCls} /></div>
                  <div><label className={lblCls}>Building</label><input type="text" name="ConsigneeFlatBuilding" value={formData.ConsigneeFlatBuilding} onChange={handleChange} className={inpCls} /></div>
                  <div><label className={lblCls}>Street</label><input type="text" name="ConsigneeStreet" value={formData.ConsigneeStreet} onChange={handleChange} className={inpCls} /></div>
                  <div><label className={lblCls}>City</label><input type="text" name="ConsigneeCity" value={formData.ConsigneeCity} onChange={handleChange} className={inpCls} /></div>
                </div>
              </FormSection>

              {/* Package Details */}
              <FormSection>
                <SectionTitle icon={Package} title="Package Details" />
                <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
                  <div className="col-span-full">
                    <label className={lblCls}>Description</label>
                    <textarea name="Description" value={formData.Description} onChange={handleChange} rows={2}
                      className={`${inpCls} resize-y min-h-[60px]`} />
                  </div>
                  <div><label className={lblCls}>Weight (kg)</label><input type="number" step="0.001" name="Weight" value={formData.Weight} onChange={handleChange} min="0" className={numCls} /></div>
                  <div><label className={lblCls}>Pieces</label><input type="number" name="Pieces" value={formData.Pieces} onChange={handleChange} min="0" className={numCls} /></div>
                  <div><label className={lblCls}>No. of Packages</label><input type="number" name="NoOfPkgs" value={formData.NoOfPkgs} onChange={handleChange} min="0" className={numCls} /></div>
                  <div><label className={lblCls}>Invoice Value</label><input type="number" step="0.01" name="InvoiceValue" value={formData.InvoiceValue} onChange={handleChange} min="0" className={numCls} /></div>
                  <div>
                    <label className={lblCls}>Payment Mode</label>
                    <select name="PaymentMode" value={formData.PaymentMode} onChange={handleChange} className={selCls}>
                      <option value="">Select</option>
                      <option value="Cash">Cash</option>
                      <option value="Credit">Credit</option>
                      <option value="Cheque">Cheque</option>
                    </select>
                  </div>
                </div>
              </FormSection>

              {/* Delivery Details */}
              <FormSection>
                <SectionTitle icon={FileText} title="Delivery Details" />
                <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
                  <div>
                    <label className={lblCls}>Collected By</label>
                    <select name="CollectedBy" value={formData.CollectedBy} onChange={handleChange} className={selCls}>
                      <option value="">Select Employee</option>
                      {employees.map(e => <option key={e.employeeid} value={e.employeeid}>{e.employeename}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={lblCls}>Received By</label>
                    <select name="ReceivedBy" value={formData.ReceivedBy} onChange={handleChange} className={selCls}>
                      <option value="">Select Employee</option>
                      {employees.map(e => <option key={e.employeeid} value={e.employeeid}>{e.employeename}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={lblCls}>Status</label>
                    <select name="CourierStatusID" value={formData.CourierStatusID} onChange={handleChange} className={selCls}>
                      <option value="">Select Status</option>
                      <option value="1">Pending</option>
                      <option value="2">In Transit</option>
                      <option value="3">Delivered</option>
                      <option value="4">Returned</option>
                    </select>
                  </div>
                  <div>
                    <label className={lblCls}>Delivery Date</label>
                    <input type="date" name="DeliveryDate" value={formData.DeliveryDate} onChange={handleChange} className={inpCls} />
                  </div>
                  <div className="col-span-full">
                    <label className={lblCls}>Remarks</label>
                    <textarea name="Remarks" value={formData.Remarks} onChange={handleChange} rows={2}
                      className={`${inpCls} resize-y min-h-[60px]`} />
                  </div>
                </div>
              </FormSection>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 bg-[#F8FAFC] border-t border-[#E2E8F0] rounded-b-xl flex-shrink-0">
              <button type="button" onClick={onClose} disabled={loading}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-gray-600 bg-white border-2 border-[#CBD5E1] rounded-lg cursor-pointer transition-all duration-150 hover:bg-gray-50 hover:border-gray-400 disabled:opacity-60 disabled:cursor-not-allowed">
                Cancel
              </button>
              {editData && (
                <button type="button" onClick={() => printConsignment(formData, CONSIGNMENT_TYPE)} disabled={loading}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white border-0 rounded-lg cursor-pointer transition-all duration-150 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ background: `linear-gradient(135deg, ${SUCCESS} 0%, ${SUCCESS_DARK} 100%)` }}>
                  <Printer size={16} /> Print Consignment
                </button>
              )}
              <button type="submit" disabled={loading || generatingNumber}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white border-0 rounded-lg cursor-pointer transition-all duration-150 hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(37,99,235,0.4)] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)` }}>
                {loading
                  ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Saving...</>
                  : <><Save size={16} />Save Domestic Consignment</>}
              </button>
            </div>
          </form>
        </div>
      </div>

      {customerModalJSX}
      {helpModalJSX}

      <style>{`@keyframes slideIn { from { opacity:0; transform:translateY(-20px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </>
  );
};

export default ConsignmentEntryFormD;